// AddUserModal.tsx
import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Region } from '../types';
import { AppContext } from '../../AppContext';

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onAddUser: (email: string, password: string, roleId: number, regionId: string) => void;
  regions: Region[];
  loading: boolean;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ open, onClose, loading, onAddUser, regions }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [regionId, setRegionId] = useState('');
  const { user: currentUser } = useContext(AppContext);

  const handleSubmit = () => {
    onAddUser(email, password, Number(roleId), regionId);
  };

  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      BackdropProps={{
        onClick: (e) => { if (loading) e.stopPropagation(); }
      }}
      onClose={handleDialogClose}
      aria-labelledby="add-user-dialog"
    >
      <DialogTitle>Add User</DialogTitle>
      <DialogContent>
        <TextField
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Role"
          fullWidth
          select
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          margin="dense"
        >
          {/* Only show SUPERADMIN if currentUserRole is 1 */}
          {currentUser ? currentUser.role_id <= 1 && <MenuItem value="1">SUPERADMIN</MenuItem> : <div/>}

          {/* Only show ADMIN if currentUserRole <= 2 */}
          {currentUser ? currentUser.role_id <= 2 && <MenuItem value="2">ADMIN</MenuItem> : <div/>}

          {/* Always show ADVANCED and MEMBER (lower roles) */}
          <MenuItem value="5">ADVANCED</MenuItem>
          <MenuItem value="3">MEMBER</MenuItem>
        </TextField>
        <TextField
          label="Region"
          fullWidth
          select
          value={regionId}
          onChange={(e) => setRegionId(e.target.value as string)}
          margin="dense"
        >
          {regions.map((region) => (
            <MenuItem key={region.id} value={region.id}>
              {region.name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || !email || !password || !roleId}
        >
          {loading ? <CircularProgress size={24} /> : 'Add User'}
        </Button>
        <Button onClick={() => !loading && onClose()} color="primary" disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
